/**
 * FlahaSOIL v2 — warning list via SpecialNote.
 *
 * Renders the `warnings: string[]` field from a calculate-response
 * or the `warningsJson` from an interpretation. Empty list renders
 * nothing. Titles use reader language (not "engine").
 */
import { Stack } from "@mui/material";

import { SpecialNote, type SpecialNoteKind } from "./SpecialNote";

interface WarningListProps {
	warnings: readonly string[] | null | undefined;
	severity?: "info" | "warning" | "error";
}

function kindForSeverity(
	severity: "info" | "warning" | "error"
): SpecialNoteKind {
	if (severity === "error") return "constraint";
	if (severity === "warning") return "limitation";
	return "methodology";
}

function titleForSeverity(severity: "info" | "warning" | "error"): string {
	if (severity === "error") return "Could not complete analysis";
	if (severity === "warning") return "Data limitation";
	return "Information";
}

export function WarningList({
	warnings,
	severity = "warning",
}: WarningListProps) {
	if (!warnings || warnings.length === 0) return null;
	const kind = kindForSeverity(severity);
	const title = titleForSeverity(severity);

	return (
		<Stack
			component="section"
			aria-label="Warnings and limitations"
			spacing={1}
		>
			{warnings.map((w, i) => (
				<SpecialNote
					key={`${i}-${w.slice(0, 24)}`}
					kind={kind}
					title={title}
					dense
					{...(i === 0 ? { "data-testid": "warning-list-first" } : {})}
				>
					{w}
				</SpecialNote>
			))}
		</Stack>
	);
}
